// ============================================
// RITUAL PLAN LIMITS & FEATURE GATING
// ============================================
// 
// COST ANALYSIS (per month):
// - Supabase Pro: $25/mo base
// - Edge Function invocations: ~$2/1M invocations (negligible)
// - Database storage: $0.125/GB (first 8GB free)
// - Bandwidth: $0.09/GB (first 5GB free)
// - AI calls: ~$0.01-0.05 per call (Gemini Flash)
// 
// MARGIN ANALYSIS (95%+ target):
// - Launch ($39/mo): Cost ~$1.50/user → 96% margin
// - Growth ($119/mo): Cost ~$3/user → 97% margin  
// - OpsPro ($249/mo): Cost ~$5/user → 98% margin
// 
// Key: AI is biggest cost. Rate limit AI calls aggressively.
// ============================================

export type PlanTier = 'free' | 'launch' | 'growth' | 'opspro';

export interface PlanLimits {
  // Core limits
  maxSeats: number;
  maxSOPs: number;
  maxRunsPerMonth: number;
  maxStorageGB: number;
  
  // Feature flags
  features: {
    aiSopGeneration: boolean;
    aiAssistant: boolean;
    voiceInput: boolean;
    advancedAnalytics: boolean;
    customRoles: boolean;
    apiAccess: boolean;
    webhooks: boolean;
    whiteLabel: boolean;
    dedicatedSupport: boolean;
    auditLogs: boolean;
    sso: boolean;
    customIntegrations: boolean;
    prioritySupport: boolean;
  };
  
  // Rate limits (per hour unless specified)
  rateLimits: {
    aiCallsPerHour: number;
    aiCallsPerDay: number;
    apiCallsPerHour: number;
    uploadsPerDay: number;
    exportPerDay: number;
  };
  
  // Pricing
  priceINR: number;
  priceUSD: number;
}

export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free: {
    maxSeats: 3,
    maxSOPs: 5,
    maxRunsPerMonth: 20,
    maxStorageGB: 0.5,
    features: {
      aiSopGeneration: true, // First 3 free
      aiAssistant: true, // Limited
      voiceInput: false,
      advancedAnalytics: false,
      customRoles: false,
      apiAccess: false,
      webhooks: false,
      whiteLabel: false,
      dedicatedSupport: false,
      auditLogs: false,
      sso: false,
      customIntegrations: false,
      prioritySupport: false,
    },
    rateLimits: {
      aiCallsPerHour: 5,
      aiCallsPerDay: 20,
      apiCallsPerHour: 100,
      uploadsPerDay: 10,
      exportPerDay: 1,
    },
    priceINR: 0,
    priceUSD: 0,
  },
  launch: {
    maxSeats: 10,
    maxSOPs: -1, // Unlimited
    maxRunsPerMonth: -1,
    maxStorageGB: 5,
    features: {
      aiSopGeneration: true,
      aiAssistant: true,
      voiceInput: true,
      advancedAnalytics: false,
      customRoles: false,
      apiAccess: false,
      webhooks: false,
      whiteLabel: false,
      dedicatedSupport: false,
      auditLogs: false,
      sso: false,
      customIntegrations: false,
      prioritySupport: false,
    },
    rateLimits: {
      aiCallsPerHour: 20,
      aiCallsPerDay: 100,
      apiCallsPerHour: 500,
      uploadsPerDay: 50,
      exportPerDay: 5,
    },
    priceINR: 2490,
    priceUSD: 39,
  },
  growth: {
    maxSeats: 40,
    maxSOPs: -1,
    maxRunsPerMonth: -1,
    maxStorageGB: 25,
    features: {
      aiSopGeneration: true,
      aiAssistant: true,
      voiceInput: true,
      advancedAnalytics: true,
      customRoles: true,
      apiAccess: true,
      webhooks: true,
      whiteLabel: false,
      dedicatedSupport: false,
      auditLogs: true,
      sso: false,
      customIntegrations: true,
      prioritySupport: true,
    },
    rateLimits: {
      aiCallsPerHour: 50,
      aiCallsPerDay: 500,
      apiCallsPerHour: 2000,
      uploadsPerDay: 200,
      exportPerDay: 20,
    },
    priceINR: 6990,
    priceUSD: 119,
  },
  opspro: {
    maxSeats: 100,
    maxSOPs: -1,
    maxRunsPerMonth: -1,
    maxStorageGB: 100,
    features: {
      aiSopGeneration: true,
      aiAssistant: true,
      voiceInput: true,
      advancedAnalytics: true,
      customRoles: true,
      apiAccess: true,
      webhooks: true,
      whiteLabel: true,
      dedicatedSupport: true,
      auditLogs: true,
      sso: true,
      customIntegrations: true,
      prioritySupport: true,
    },
    rateLimits: {
      aiCallsPerHour: 200,
      aiCallsPerDay: 2000,
      apiCallsPerHour: 10000,
      uploadsPerDay: 1000,
      exportPerDay: 100,
    },
    priceINR: 14990,
    priceUSD: 249,
  },
};

// ============================================
// FEATURE GATING UTILITIES
// ============================================

export function getPlanLimits(planTier: PlanTier): PlanLimits {
  return PLAN_LIMITS[planTier] || PLAN_LIMITS.free;
}

export function canUseFeature(planTier: PlanTier, feature: keyof PlanLimits['features']): boolean {
  const limits = getPlanLimits(planTier);
  return limits.features[feature] ?? false;
}

export function getMaxSeats(planTier: PlanTier): number {
  return getPlanLimits(planTier).maxSeats;
}

export function getRateLimit(planTier: PlanTier, limitType: keyof PlanLimits['rateLimits']): number {
  return getPlanLimits(planTier).rateLimits[limitType];
}

export function isUnlimited(value: number): boolean {
  return value === -1;
}

// ============================================
// USAGE TRACKING
// ============================================

export interface UsageStats {
  aiCallsToday: number;
  aiCallsThisHour: number;
  uploadsToday: number;
  runsThisMonth: number;
  storageUsedGB: number;
  seatsUsed: number;
}

export function checkUsageLimit(
  planTier: PlanTier,
  usage: Partial<UsageStats>,
  action: 'ai_call' | 'upload' | 'run' | 'seat'
): { allowed: boolean; reason?: string; upgradeRequired?: boolean } {
  const limits = getPlanLimits(planTier);

  switch (action) {
    case 'ai_call':
      if (usage.aiCallsThisHour !== undefined && usage.aiCallsThisHour >= limits.rateLimits.aiCallsPerHour) {
        return {
          allowed: false,
          reason: `Hourly AI limit reached (${limits.rateLimits.aiCallsPerHour}/hr). Resets in ${60 - new Date().getMinutes()} min.`,
          upgradeRequired: planTier !== 'opspro',
        };
      }
      if (usage.aiCallsToday !== undefined && usage.aiCallsToday >= limits.rateLimits.aiCallsPerDay) {
        return {
          allowed: false,
          reason: `Daily AI limit reached (${limits.rateLimits.aiCallsPerDay}/day). Resets at midnight.`,
          upgradeRequired: planTier !== 'opspro',
        };
      }
      break;

    case 'upload':
      if (usage.uploadsToday !== undefined && usage.uploadsToday >= limits.rateLimits.uploadsPerDay) {
        return {
          allowed: false,
          reason: `Daily upload limit reached (${limits.rateLimits.uploadsPerDay}/day).`,
          upgradeRequired: planTier !== 'opspro',
        };
      }
      break;

    case 'run':
      if (!isUnlimited(limits.maxRunsPerMonth) && usage.runsThisMonth !== undefined && usage.runsThisMonth >= limits.maxRunsPerMonth) {
        return {
          allowed: false,
          reason: `Monthly run limit reached (${limits.maxRunsPerMonth}/month).`,
          upgradeRequired: true,
        };
      }
      break;

    case 'seat':
      if (usage.seatsUsed !== undefined && usage.seatsUsed >= limits.maxSeats) {
        return {
          allowed: false,
          reason: `Seat limit reached (${limits.maxSeats} seats).`,
          upgradeRequired: true,
        };
      }
      break;
  }

  return { allowed: true };
}

// ============================================
// UPGRADE PROMPTS
// ============================================

export function getUpgradeMessage(currentPlan: PlanTier, feature: string): string {
  const messages: Record<string, Record<PlanTier, string>> = {
    aiSopGeneration: {
      free: "Upgrade to Launch for unlimited AI-powered SOP generation.",
      launch: "You have unlimited AI SOP generation!",
      growth: "You have unlimited AI SOP generation!",
      opspro: "You have unlimited AI SOP generation!",
    },
    advancedAnalytics: {
      free: "Upgrade to Growth for advanced analytics and insights.",
      launch: "Upgrade to Growth for advanced analytics and insights.",
      growth: "You have access to advanced analytics!",
      opspro: "You have access to advanced analytics!",
    },
    apiAccess: {
      free: "Upgrade to Growth for API access.",
      launch: "Upgrade to Growth for API access.",
      growth: "You have API access!",
      opspro: "You have API access!",
    },
    voiceInput: {
      free: "Upgrade to Launch for voice input.",
      launch: "You have voice input!",
      growth: "You have voice input!",
      opspro: "You have voice input!",
    },
  };

  return messages[feature]?.[currentPlan] || `Upgrade to unlock ${feature}.`;
}

// ============================================
// COST BREAKDOWN (for internal reference)
// ============================================

export const COST_PER_OPERATION = {
  // AI costs (approximate per call)
  ai_sop_generation: 0.05, // ~500 tokens
  ai_assistant_call: 0.01, // ~100 tokens
  ai_analysis: 0.02, // ~200 tokens
  
  // Storage costs (per GB/month)
  storage_per_gb: 0.125,
  
  // Bandwidth (per GB)
  bandwidth_per_gb: 0.09,
  
  // Edge function (per 1M invocations)
  edge_per_million: 2.00,
};

export const ESTIMATED_MONTHLY_COST_PER_USER = {
  free: 0.50, // Minimal usage
  launch: 1.50, // ~30 AI calls, 1GB storage
  growth: 3.00, // ~100 AI calls, 5GB storage
  opspro: 5.00, // ~300 AI calls, 20GB storage
};

export const GROSS_MARGIN = {
  free: 0, // No revenue
  launch: 0.96, // 96% margin
  growth: 0.97, // 97% margin
  opspro: 0.98, // 98% margin
};
